import { supabase } from '../lib/supabase'
import { Shoot, CreateShootData } from '../types'

export const shootService = {
  // Update past shoots to completed status
  async updatePastShootsToCompleted(userId: string): Promise<void> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const today = new Date()
    const formatDateForQuery = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    try {
      // Update all scheduled shoots that are in the past to completed
      const { error } = await supabase
        .from('shoots')
        .update({ 
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('status', 'scheduled')
        .lt('date', formatDateForQuery(today))
      
      if (error) {
        console.error('Error updating past shoots to completed:', error)
        throw error
      }
    } catch (error) {
      console.error('Failed to update past shoots status:', error)
      // Don't throw here to prevent breaking the main functionality
      // The status update is a nice-to-have feature, not critical
    }
  },

  // Get all shoots for the current user
  async getShootsForUser(userId: string): Promise<Shoot[]> {
    if (!supabase) throw new Error('Supabase not configured')
    
    // First update past shoots to completed
    await this.updatePastShootsToCompleted(userId)
    
    const { data, error } = await supabase
      .from('shoots')
      .select(`
        *,
        client:clients(*)
      `)
      .eq('user_id', userId)
      .order('date', { ascending: true })
    
    if (error) throw error
    return data || []
  },

  // Check for conflicting shoots (double booking)
  async checkForConflictingShoots(
    userId: string, 
    date: string, 
    startTime: string, 
    endTime: string,
    excludeShootId?: string
  ): Promise<Shoot[]> {
    if (!supabase) throw new Error('Supabase not configured')
    
    let query = supabase
      .from('shoots')
      .select(`
        *,
        client:clients(*)
      `)
      .eq('user_id', userId)
      .eq('date', date)
      .neq('status', 'cancelled')
    
    // Exclude current shoot if editing
    if (excludeShootId) {
      query = query.neq('id', excludeShootId)
    }
    
    const { data, error } = await query
    
    if (error) throw error
    
    // Filter for time overlaps
    const conflictingShoots = (data || []).filter(shoot => {
      const existingStart = shoot.start_time
      const existingEnd = shoot.end_time
      
      // Check if times overlap
      // Two time ranges overlap if: start1 < end2 && start2 < end1
      return startTime < existingEnd && existingStart < endTime
    })
    
    return conflictingShoots
  },

  // Get upcoming shoots (within next 30 days)
  async getUpcomingShootsForUser(userId: string): Promise<Shoot[]> {
    if (!supabase) throw new Error('Supabase not configured')
    
    // First update past shoots to completed
    await this.updatePastShootsToCompleted(userId)
    
    const today = new Date()
    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(today.getDate() + 30)
    
    // Use local timezone date formatting to avoid timezone offset issues
    const formatDateForQuery = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    const { data, error } = await supabase
      .from('shoots')
      .select(`
        *,
        client:clients(*)
      `)
      .eq('user_id', userId)
      .gte('date', formatDateForQuery(today))
      .lte('date', formatDateForQuery(thirtyDaysFromNow))
      .eq('status', 'scheduled')
      .order('date', { ascending: true })
    
    if (error) throw error
    return data || []
  },

  // Create a new shoot
  async createShoot(shootData: CreateShootData, userId: string): Promise<Shoot> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('shoots')
      .insert({
        ...shootData,
        user_id: userId,
        status: 'scheduled'
      })
      .select(`
        *,
        client:clients(*)
      `)
      .single()
    
    if (error) throw error
    return data
  },

  // Update a shoot
  async updateShoot(shootId: string, updates: Partial<CreateShootData>): Promise<Shoot> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('shoots')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', shootId)
      .select(`
        *,
        client:clients(*)
      `)
      .single()
    
    if (error) throw error
    return data
  },

  // Update shoot details (new fields)
  async updateShootDetails(shootId: string, details: {
    shot_list?: any[]
    kit_list?: any[]
    weather_notes?: string
    parking_info?: string
    access_instructions?: string
    emergency_contact?: string
    special_requirements?: string
    client_prep_notes?: string
    backup_plan?: string
    post_shoot_notes?: string
  }): Promise<Shoot> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('shoots')
      .update({
        ...details,
        updated_at: new Date().toISOString()
      })
      .eq('id', shootId)
      .select(`
        *,
        client:clients(*)
      `)
      .single()
    
    if (error) throw error
    return data
  },

  // Get shoot by ID with full details
  async getShootById(shootId: string): Promise<Shoot | null> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { data, error } = await supabase
      .from('shoots')
      .select(`
        *,
        client:clients(*)
      `)
      .eq('id', shootId)
      .single()
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null
      }
      throw error
    }
    
    return data
  },

  // Delete a shoot
  async deleteShoot(shootId: string): Promise<void> {
    if (!supabase) throw new Error('Supabase not configured')
    
    const { error } = await supabase
      .from('shoots')
      .delete()
      .eq('id', shootId)
    
    if (error) throw error
  },

  // Get shoots with kit completion stats
  async getShootsWithKitStats(userId: string): Promise<Shoot[]> {
    if (!supabase) throw new Error('Supabase not configured')
    
    // First update past shoots to completed
    await this.updatePastShootsToCompleted(userId)
    
    const { data, error } = await supabase
      .from('shoot_details_view')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: true })
    
    if (error) throw error
    return data || []
  }
} 